/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Collapsible from 'components/Collapsible/Collapsible';
import Picture from 'components/Picture/Picture';
import scrollTo from 'utils/scrollTo';
import useGuid from 'effects/useGuid';
import '../Ensemble.scss';
import './Actors.scss';

/**
 * This is the Actors component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Actors({
  id,
  level,
  heading,
  previous: previousHeading,
  list,
  more,
  container,
}) {
  const guid = useGuid('ensemble-actors');
  const node = useRef(null);
  const button = useRef(null);
  const [focus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const Header = `h${level}`;

  useEffect(() => {
    function onFocus() {
      if (!node.current.contains(document.activeElement)) {
        setFocus(false);
      }
    }

    if (focus && open) {
      document.body.addEventListener('focusin', onFocus);
    }

    return () => {
      document.body.removeEventListener('focusin', onFocus);
    };
  }, [focus, open]);

  useEffect(() => {
    function onChange() {
      if (`#${id}` === window.location.hash) {
        setOpen(true);
      }
    }

    window.addEventListener('hashchange', onChange);
    onChange();

    return () => {
      window.removeEventListener('hashchange', onChange);
    };
  }, [id]);

  useEffect(() => {
    let timeout = null;
    function onOpen() {
      timeout = setTimeout(() => {
        setOpen(true);
      }, 1000);
    }

    if (container) {
      container.addEventListener('open', onOpen);
    }

    return function cleanup() {
      clearTimeout(timeout);
      if (container) {
        container.removeEventListener('open', onOpen);
      }
    };
  }, []);

  useEffect(() => {
    if (opened) {
      const showMenu = document.getElementById('show-sub-menu');
      if (showMenu) {
        scrollTo(node.current, {
          alignTop: true,
          offset: showMenu.offsetHeight + 70,
        });
      } else {
        scrollTo(node.current, { alignTop: true, offset: 180 });
      }
    }
  }, [opened]);

  const current = list.filter(({ active }) => active);
  const previous = list.filter(({ active }) => !active);

  function renderPeriod(period) {
    if (!period) return null;

    return <div className="period">{period}</div>;
  }

  function renderPrevious() {
    if (previous.length === 0) return null;

    const moreTitle = more.replace('{title}', name);

    return (
      <div className="previous">
        <h4 className="h3">{previousHeading}</h4>
        <dl>
          {previous.map(({ id, url, name, role, period }) => (
            <div key={id}>
              <dt>{role}</dt>
              <dd>
                <a
                  key={id}
                  href={url}
                  title={moreTitle}
                  aria-label={moreTitle}
                  tabIndex={open ? null : '-1'}
                >
                  {name}
                </a>
                {renderPeriod(period)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }

  return (
    <section
      ref={node}
      className={classnames('ensemble', 'actors', { open })}
      onFocus={() => {
        setFocus(true);
      }}
    >
      <button
        ref={button}
        id={`button${guid}`}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={`details${guid}`}
        onClick={() => {
          setOpen(open => !open);
        }}
      >
        <Header className="h3">
          <span />
          {heading}
        </Header>
      </button>
      <Collapsible
        id={`details${guid}`}
        role="region"
        aria-labelledby={`button${guid}`}
        open={open}
        active={focus && open}
        onOpen={() => {
          setOpened(true);
        }}
        onClose={() => {
          setOpen(false);
          if (button.current) {
            button.current.focus();
          }
        }}
        onClosed={() => {
          setOpened(false);
        }}
      >
        <div className="current">
          {current.map(({ id, url, image, name, role }) => (
            <a
              key={id}
              href={url}
              className="actor"
              title={more.replace('{title}', name)}
              aria-label={more.replace('{title}', name)}
              tabIndex={open ? null : '-1'}
            >
              <div className="image flap-top-left flap-bottom-right">
                <div>
                  <Picture {...image} />
                </div>
              </div>
              <div className="text">
                <h4 className="hd-sm">{name}</h4>
                <div className="body-sm role">{role}</div>
              </div>
            </a>
          ))}
        </div>
        {renderPrevious()}
      </Collapsible>
    </section>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Actors.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  level: PropTypes.number.isRequired,
  heading: PropTypes.string.isRequired,
  previous: PropTypes.string.isRequired,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      url: PropTypes.string.isRequired,
      image: PropTypes.object,
      name: PropTypes.string,
      role: PropTypes.string,
      active: PropTypes.bool,
    })
  ).isRequired,
  more: PropTypes.string.isRequired,
  container: PropTypes.object,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Actors.defaultProps = {
  id: null,
  level: 3,
  heading: 'Actors',
  previous: 'Previous actors',
  list: [],
  more: 'Read more about «{title}»',
  container: null,
};

export default Actors;
